import InfoContext from "pages/InfoContext";
import InfoHeader from "components/info-header/InfoHeader";
import _ from "lodash";
import { ENTITY } from "constants/info-header";
import {
  DATATRACK_MODELS,
  FILTERS_FOR_NAMES,
  MODAL_CONTENT,
  HISTORY_SUBCONTRACTOR_BY_FILTERS_KEYS_ID,
} from "constants/constants";
import { ROLE_MASCARADE } from "access-roles/roles";
import { ROLE_PLPM } from "constants/forms";
import { useContext, useEffect, useState, useMemo, useRef } from "react";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import { useRightsContext } from "api/rights/RightsProvider";
import { useSubcontractorCompanyContext } from "api/directory/subcontractor-company/SubcontractorCompanyProvider";
import { useSubcontractorCompanyDatatrackContext } from "api/datatrack/subcontractor-company/SubcontractorCompanyDatatrackProvider";
import { useUserContext } from "api/user/UserProvider";
import { useUserDatatrackContext } from "api/datatrack/UserDatatrackProvider";
import { getUserDatatrackQuery } from "api/datatrack/queries";
import { validateTables } from "utils/functions";
import { useMissionManagerContext } from "api/mission/mission-manager/MissionManagerProvider";
import { useMissionFeeContext } from "api/mission/mission-fee/MissionFeeProvider";
import {
  formatMissionFeeToCreate,
  formatMissionManagerToCreate,
} from "utils/missionHelper";
import { useMissionContext } from "api/mission/MissionProvider";
import {
  formatListWithUpdatedUser,
  formatOptionsTranslated,
} from "utils/staffHelper";
import { useDomainContext } from "api/datatrack/domain/DomainProvider";
import { useBillingClientDatatrackContext } from "api/datatrack/client/BillingClientDatatrackProvider";
import { useBillingClientContext } from "api/directory/billing-client/BillingClientProvider";
import { formatBillingHistoryToCreate } from "utils/clientHelper";

const InfoHeaderAction = () => {
  const { missionBySlug } = useMissionContext();
  const { getNamesByFilters, namesByFilters } = useGeneralContext();
  const { getDomainListByUserId, domainList } = useDomainContext();
  const {
    missionFeeByMissionId,
    createMissionFee,
    getMissionFeeByMissionId,
    editMissionFee,
    deleteMissionFee,
  } = useMissionFeeContext();
  const {
    missionManagerByMissionId,
    createMissionManager,
    getMissionManagerByMissionId,
    editMissionManager,
    deleteMissionManager,
  } = useMissionManagerContext();
  const {
    userDatatrack,
    userDatatrackCreate,
    userDatatrackDeleteById,
    userDatatrackGetter,
    userDatatrackUpdateById,
  } = useUserDatatrackContext();
  const {
    getHistoryBillingClientByClientId,
    createClientByBillingClientId,
    editClientByBillingClientId,
    deleteClientByBillingClientId,
    historyBillingClientByClientId,
  } = useBillingClientDatatrackContext();
  const {
    billingClientsByName,
    getBillingClientByIdArray,
    billingClientsByIds,
    searchBillingClientByName,
  } = useBillingClientContext();
  const {
    historySubCompany,
    createHistorySubcontractorCompany,
    deleteHistorySubcontractorCompany,
    editHistorySubcontractorCompany,
    getHistorySubcontractorByFilters,
  } = useSubcontractorCompanyDatatrackContext();
  const {
    subcontractorCompanies,
    searchSubcontractorCompanyByName,
    getSubcontractorCompaniesByFilters,
  } = useSubcontractorCompanyContext();
  const { canMascaradeAs, setCanMascarade, canMascarade } = useRightsContext();
  const { mascaradeAs, userScope, connectedUser, userRoleList } =
    useUserContext();
  const {
    davFunctions,
    lang,
    searchUsers,
    usersByIds,
    setHeaderRoles,
    setIsEnabled,
    clientBySlug,
    id,
    email,
    userById,
    setRefresh,
    subcontractorBySlug,
    setCode,
    setStatus,
    entity,
    entitySlug,
    entityStatus,
    missionIsBackupable,
  } = useContext(InfoContext);
  const [modalType, setModalType] = useState({});
  const [namesForManagers, setNamesForManagers] = useState([]);
  const [namesForUpdatedBy, setNamesForUpdatedBy] = useState([]);
  const [elementsToDelete, setElementsToDelete] = useState([]);
  const [managersContracts, setManagersContracts] = useState([]);
  const requestedUserIds = useRef(new Set());

  const getModalInfo = (info) => {
    switch (info) {
      case MODAL_CONTENT.MANAGE_COMPANY_HISTORY:
        getHistorySubcontractorByFilters(id);
        break;
      case MODAL_CONTENT.MANAGE_DAILY_FEE:
        getMissionFeeByMissionId(missionBySlug?.id);
        break;
      case MODAL_CONTENT.MANAGE_HISTORY_MANAGERS:
        getMissionManagerByMissionId({
          id: missionBySlug?.id,
        });
        break;
      case MODAL_CONTENT.MANAGE_JOB_HISTORY:
        getDomainListByUserId({ id });
        break;
      case MODAL_CONTENT.MANAGE_BILLING_CLIENT_HISTORY:
        getHistoryBillingClientByClientId({
          id: clientBySlug?.id,
          scope: userScope,
        });
        break;
      case MODAL_CONTENT.MANAGE_MANAGERS:
      default:
        userDatatrackGetter({ id: id, model: info });
        break;
    }
  };

  const allUserIds = useMemo(() => {
    const dataSources = [
      { data: userDatatrack[modalType], key: "updated_by" },
      { data: missionFeeByMissionId, key: "updated_by" },
      { data: missionManagerByMissionId, key: "updated_by" },
      { data: domainList, key: "updated_by" },
      { data: historyBillingClientByClientId, key: "updated_by" },
      { data: historySubCompany, key: "updated_by" },
    ];

    const extractUserIds = (dataSource) => {
      if (!dataSource.data?.length) return [];
      return dataSource.data
        .map((item) => item[dataSource.key])
        .filter(Boolean);
    };

    const allIds = dataSources.flatMap(extractUserIds);
    return [...new Set(allIds)];
  }, [
    userDatatrack[modalType],
    missionFeeByMissionId,
    missionManagerByMissionId,
    domainList,
    historyBillingClientByClientId,
    historySubCompany,
  ]);

  useEffect(() => {
    if (allUserIds.length > 0) {
      const newIds = allUserIds.filter(
        (id) => !requestedUserIds.current.has(id)
      );
      newIds?.forEach((id) => requestedUserIds.current.add(id));
      Array.isArray(newIds) &&
        newIds?.length > 0 &&
        searchUsers({ ids: newIds });
    }
  }, [allUserIds]);

  useEffect(() => {
    if (historyBillingClientByClientId?.length > 0) {
      getBillingClientByIdArray(
        _.chain(historyBillingClientByClientId)
          .map("billing_client_id")
          .uniqWith(_.isEqual)
          .join(",")
          .value()
      );
    }
  }, [historyBillingClientByClientId]);

  useEffect(() => {
    const ids = userDatatrack?.manager?.map((elem) => elem?.manager_id);
    if (ids?.length > 0) {
      const fetchNames = async () => {
        const result = await getNamesByFilters({
          types: [FILTERS_FOR_NAMES.PERSON],
          id: ids,
          assigned_branches_include_contract: true,
        });
        setNamesForManagers(result);
      };
      fetchNames();
    }
  }, [userDatatrack]);

  const onBulkCreateModal = async ({ data, type }) => {
    switch (type) {
      case MODAL_CONTENT.MANAGE_BILLING_CLIENT_HISTORY:
        await createClientByBillingClientId({
          data: formatBillingHistoryToCreate({
            ...data,
            client_id: clientBySlug?.id,
          }),
          scope: userScope,
        });
        break;
      case MODAL_CONTENT.MANAGE_HISTORY_MANAGERS:
        await createMissionManager(
          formatMissionManagerToCreate({
            data,
            missionId: missionBySlug?.id,
            managerId: data?.manager_id,
          })
        );
        break;
      case MODAL_CONTENT.MANAGE_COMPANY_HISTORY:
        await createHistorySubcontractorCompany({
          idKey: HISTORY_SUBCONTRACTOR_BY_FILTERS_KEYS_ID.subcontractorId,
          subcontractor_id: id,
          subcontractor_company_id: data?.subcontractor_company_id,
          establishment_date: data?.establishment_date,
        });
        break;
      case MODAL_CONTENT.MANAGE_DAILY_FEE:
        await createMissionFee(
          formatMissionFeeToCreate({
            data,
            missionId: missionBySlug?.id,
            managerId: missionBySlug?.manager_id,
          })
        );
        break;
      default:
        await userDatatrackCreate({
          data: { ...data, person_id: data.person_id || id },
          model: type,
        });
        break;
    }
  };

  const onBulkEditModal = async ({ data, type }) => {
    if (data) {
      switch (type) {
        case MODAL_CONTENT.MANAGE_COMPANY_HISTORY:
          await editHistorySubcontractorCompany({
            id: data?.id,
            data: {
              subcontractor_company_id: data?.subcontractor_company_id,
              subcontractor_id: data?.subcontractor_id,
              establishment_date: data?.establishment_date,
            },
          });
          break;
        case MODAL_CONTENT.MANAGE_DAILY_FEE:
          await editMissionFee({
            id: data?.id,
            data: formatMissionFeeToCreate({
              data: data,
              missionId: missionBySlug?.id,
              managerId: missionBySlug?.manager_id,
            }),
          });
          break;
        case MODAL_CONTENT.MANAGE_HISTORY_MANAGERS:
          await editMissionManager({
            id: data.id,
            data: formatMissionManagerToCreate({
              data: data,
              missionId: missionBySlug?.id,
              managerId: data?.manager_id,
            }),
          });
          break;
        case MODAL_CONTENT.MANAGE_BILLING_CLIENT_HISTORY:
          await editClientByBillingClientId({
            id: data.id,
            data: data,
            scope: userScope,
          });
          break;
        default:
          await userDatatrackUpdateById({
            id: data?.id,
            data: _.omit(data, [
              "created_at",
              "created_by",
              "updated_at",
              "updated_by",
            ]),
            model: type,
          });
          break;
      }
    }
  };

  const onBulkDelete = async ({ data, type }) => {
    switch (type) {
      case MODAL_CONTENT.MANAGE_DAILY_FEE:
        await deleteMissionFee(data.id);
        break;
      case MODAL_CONTENT.MANAGE_HISTORY_MANAGERS:
        await deleteMissionManager(data.id);
        break;
      case MODAL_CONTENT.MANAGE_BILLING_CLIENT_HISTORY:
        await deleteClientByBillingClientId({ id: data?.id, scope: userScope });
        break;
      case MODAL_CONTENT.MANAGE_COMPANY_HISTORY:
        await deleteHistorySubcontractorCompany(data.id);
        break;
      default:
        await userDatatrackDeleteById({
          id: Number(data?.id),
          model: type,
        });
        break;
    }
  };

  const validateContractTable = async ({ data }) => {
    const user_id = id;
    const formattedData = data?.map((e) => ({
      ...e,
      person_id: e.person_id || user_id,
      end_date: e.end_date || null,
      leaving_reason_id: e.leaving_reason_id || null,
    }));
    await userDatatrackUpdateById({
      data: formattedData,
      model: DATATRACK_MODELS.CONTRACT,
    });
    setRefresh(true);
  };

  const onValidate = async ({
    initialTable,
    editableTable,
    elementsToDelete,
    setElementsToDelete,
    type,
  }) => {
    if (type === DATATRACK_MODELS.CONTRACT) {
      return validateContractTable({ data: editableTable });
    }
    return validateTables({
      initialTable,
      editableTable,
      elementsToDelete,
      setElementsToDelete,
      setRefresh,
      onCreate: (data) => onBulkCreateModal({ data, type }),
      onEdit: (data) => onBulkEditModal({ data, type }),
      onDelete: (data) => onBulkDelete({ data, type }),
    });
  };

  useEffect(() => {
    switch (entity) {
      case ENTITY.CONSULTANT:
      case ENTITY.STAFF:
        setIsEnabled(userById.enabled);
        break;
      case ENTITY.SUBCONTRACTOR:
        setHeaderRoles(userById?.role?.filter((elm) => elm === ROLE_PLPM));
        break;
      default:
        break;
    }
  }, [userById]);

  useEffect(() => {
    const ids = missionManagerByMissionId?.map((elem) => elem?.manager_id);

    if (ids?.length > 0) {
      getNamesByFilters({
        types: [FILTERS_FOR_NAMES.PERSON],
        id: ids,
      });
    }
  }, [missionManagerByMissionId]);

  useEffect(() => {
    if (modalType === MODAL_CONTENT.MANAGE_COMPANY_HISTORY) {
      const ids = Array.isArray(historySubCompany)
        ? historySubCompany.map((e) => e.subcontractor_company_id)
        : [];
      ids.length > 0 &&
        getSubcontractorCompaniesByFilters({
          isMultipleIds: true,
          filters: { id: ids, include_deleted: true },
        });
    }
  }, [historySubCompany, modalType]);

  useEffect(() => {
    if (_.includes(userRoleList, ROLE_MASCARADE)) return;
    setCanMascarade(canMascaradeAs(email));
  }, [userRoleList, email]);

  useEffect(() => {
    const updatedIds = _.chain(userDatatrack?.[modalType])
      .map("updated_by")
      .filter(Boolean)
      .uniq()
      .value();
    if (updatedIds.length > 0) {
      const fetchNames = async () => {
        const result = await getNamesByFilters({
          types: [FILTERS_FOR_NAMES.PERSON],
          id: updatedIds,
        });
        setNamesForUpdatedBy(result);
      };
      fetchNames();
    }
  }, [userDatatrack, modalType]);

  const getManagersContract = async (data) => {
    const results = await Promise.all(
      data.map((e) =>
        e.manager_id
          ? getUserDatatrackQuery({
              id: e.manager_id,
              model: DATATRACK_MODELS.CONTRACT,
              userScope,
            }).catch(() => undefined)
          : Promise.resolve(undefined)
      )
    );
    setManagersContracts(
      results.map((res, i) => ({
        person_id: data[i].manager_id,
        contracts: res ?? [],
      }))
    );
  };

  const isManagerActiveContract = ({ managerId, establishmentDate }) => {
    const currentManager = managersContracts.find(
      (e) => e.person_id === managerId
    );
    const contracts = currentManager?.contracts ?? [];
    if (contracts.length === 0) return false;
    return !contracts.some(
      (c) =>
        (!c.start_date || establishmentDate >= c.start_date) &&
        (!c.end_date || establishmentDate <= c.end_date)
    );
  };

  return (
    <InfoHeader
      entity={entity}
      entitySlug={entitySlug}
      entityStatus={entityStatus}
      setElementsToDelete={setElementsToDelete}
      elementsToDelete={elementsToDelete}
      getModalInfo={getModalInfo}
      onValidate={onValidate}
      modalType={modalType}
      setModalType={setModalType}
      userDatatrack={formatListWithUpdatedUser(
        userDatatrack[modalType] || [],
        namesForUpdatedBy
      )}
      missionFeeByMissionId={missionFeeByMissionId}
      missionManagerByMissionId={formatListWithUpdatedUser(
        missionManagerByMissionId || [],
        usersByIds
      )}
      domainList={domainList}
      historySubCompany={historySubCompany}
      subcontractorCompanies={subcontractorCompanies}
      user={
        subcontractorBySlug || {
          ...userById,
          dav_function: formatOptionsTranslated({
            id: Number(userById?.dav_function),
            list: davFunctions,
            lang,
          }),
        }
      }
      mascaradeAs={() => mascaradeAs(userById?.email)}
      userScope={userScope}
      userRoleList={userRoleList}
      searchSubcontractorCompanyByName={searchSubcontractorCompanyByName}
      canMascarade={canMascarade}
      searchBillingClient={searchBillingClientByName}
      billingClients={billingClientsByName}
      billingClientsByIds={billingClientsByIds}
      historyBillingClientByClientId={historyBillingClientByClientId}
      isManagerActiveContract={isManagerActiveContract}
      getManagersContract={getManagersContract}
      managersContracts={managersContracts}
      setManagersContracts={setManagersContracts}
      missionIsBackupable={missionIsBackupable}
      connectedUser={connectedUser}
      setCode={setCode}
      setStatus={setStatus}
      managersNames={namesForManagers}
      historyManagersNames={namesByFilters}
      userCoefPositionList={
        userDatatrack[MODAL_CONTENT.MANAGE_POSITION_COEF] || []
      }
      userSalary={userDatatrack[MODAL_CONTENT.MANAGE_SALARY] || []}
    />
  );
};

export default InfoHeaderAction;
